import React, { useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
} from "react-leaflet";
import L from "leaflet";

interface Point {
  id: number;
  nom?: string;
  qr?: string;
  validated: boolean;
  latitude?: number;
  longitude?: number;
}

interface Props {
  missionId?: number;
  points?: Point[];
}
 
const MissionPointsCard: React.FC<Props> = ({ points = [] }) => {
  if (!points.length) return null;

  const totalValides = points.filter((p) => p.validated).length;
  const progression = Math.round(
    (totalValides / points.length) * 100
  );

  const pointsGeo = useMemo(
    () =>
      points.filter(
        (p) =>
          p.latitude !== undefined &&
          p.longitude !== undefined
      ),
    [points]
  );

  const progressColor =
    progression === 100
      ? "bg-green-500"
      : progression >= 50
      ? "bg-yellow-500"
      : "bg-red-500";

  // Icônes Leaflet
  const iconValide = new L.Icon({
    iconUrl:
      "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    iconSize: [32, 32],
  });

  const iconNonValide = new L.Icon({
    iconUrl:
      "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    iconSize: [32, 32],
  });

  return (
    <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700 space-y-4">
      
      {/* 📊 Progression */}
      <div>
        <h2 className="font-semibold mb-2">
          📍 Points terrain ({totalValides}/{points.length})
        </h2>

        <div className="w-full bg-slate-700 rounded-full h-3">
          <div
            className={`${progressColor} h-3 rounded-full transition-all duration-500`}
            style={{ width: `${progression}%` }}
          />
        </div>

        <div className="text-xs text-gray-400 mt-1">
          Progression : {progression}%
        </div>
      </div>

      {/* 🗺 Carte */}
      {pointsGeo.length > 0 && (
        <div className="h-80 rounded-lg overflow-hidden border border-slate-700">
          <MapContainer
            center={[
              pointsGeo[0].latitude!,
              pointsGeo[0].longitude!,
            ]}
            zoom={13}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution="© OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {pointsGeo.map((p) => (
              <Marker
                key={p.id}
                position={[p.latitude!, p.longitude!]}
                icon={
                  p.validated ? iconValide : iconNonValide
                }
              >
                <Popup>
                  <strong>
                    {p.nom || p.qr || "Point"}
                  </strong>
                  <br />
                  {p.validated
                    ? "✅ Validé"
                    : "❌ Non validé"}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* 📊 Stats */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="bg-slate-800 p-3 rounded text-center">
          <div className="text-xl font-bold text-green-400">
            {totalValides}
          </div>
          <div className="text-gray-400">
            Points validés
          </div>
        </div>

        <div className="bg-slate-800 p-3 rounded text-center">
          <div className="text-xl font-bold text-red-400">
            {points.length - totalValides}
          </div>
          <div className="text-gray-400">
            Points restants
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionPointsCard;